/**
 * `IssueTrackerCard` — the workspace's inbound issue-tracker connections in
 * Workspace Settings → Integrations (#1558 Slice 1a #1559). One card per
 * tracker the product can import from, in the order the server describes
 * them ("Jira", then "GitHub Issues"), per docs/mockups/2776-github-issues.html
 * section A. Each card mirrors screen A1 of
 * docs/mockups/1558-issue-tracker-integration.html.
 *
 * Self-contained for its data: it loads its own connection list and reads
 * the trackers' descriptions through `useTrackerDescriptors`. The tracker's
 * name, logo and words all come from its description, so nothing here names
 * one. Each card shows one of two states, matching the GitHub/Slack cards on
 * this page:
 *   - **Connected** → a `<ProviderBlock>` with where it reads from, the
 *     last-checked time, and a kebab (Test connection / Update credentials /
 *     Disconnect). "Update credentials" appears only for a tracker with
 *     sign-in fields.
 *   - **Not connected** → a centered call to action.
 *
 * Two kinds of tracker sign in differently, and the description says which:
 *   - **Typed-in fields** (`credentialSchema`, Jira): Connect opens a dialog
 *     with those fields, validates, then saves.
 *   - **An app install** (`credentialSource`): there is nothing to type. The
 *     card reads the install from the page, which owns the install record,
 *     and Connect validates and saves straight away. It shows when the
 *     install is missing, when it can't read issues yet, and when it was
 *     removed after connecting.
 * A tracker with both (GitHub Issues) offers a choice before connecting,
 * per docs/mockups/github-issues-sign-in.html Option B: the app install,
 * picked and marked recommended, or a personal token. Once connected, the
 * connection's saved `signIn` decides which of the two the card shows, and
 * a token connection offers to switch to the install.
 *
 * **The install's own card.** When the page passes an `installSlot`, the
 * tracker that signs in through the install becomes the one card for that
 * supplier (docs/mockups/github-one-card.html). It is titled by the
 * supplier's name, shows the install's repositories, menu and the apps
 * using it, says what each sign-in allows, and its Disconnect removes the
 * install along with the connection. The page supplies those parts, so
 * this file still names no supplier.
 *
 * Stage-1 only: connecting proves the tracker accepts the sign-in — it does
 * NOT check any project (that is the app binding's stage-2, Slice 1b).
 *
 * Admin-only in practice (the routes gate on `issueTrackers.write`); a
 * non-admin simply gets a 403 from the mutating calls, surfaced as an
 * inline error.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { KeyRound, RefreshCw, Trash2, CheckCircle2, AlertCircle } from 'lucide-react'
import {
  Button,
  Input,
  Label,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '@/components/ui'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { ProviderBlock, ConnectedBadge, type ProviderBlockMenuItem } from '@/components/ProviderBlock'
import { TrackerLogo, trackerButtonClass } from '@/components/TrackerLogo'
import {
  issueTrackersAPI,
  githubInstallationAPI,
  type GitHubInstallationAccount,
  type IssueTrackerConnection,
  type IssueTrackerProviderDescriptor,
  type IssueTrackerCredentialField,
  type IssueTrackerValidationResult,
} from '@/lib/api'
import { fmtDate } from './ciIntegrationsUtils'
import { useUserTimezone } from '@/hooks/useUserTimezone'
import {
  useTrackerDescriptorsState,
  trackerWords,
  descriptorFor,
  connectionNameOf,
  signInOf,
  offersSignInChoice,
} from '@/hooks/useTrackerDescriptors'
import { ProviderCardShell } from './ProviderCardShell'
import { buildManageOnGitHubUrl } from './GitHubInstall'

/**
 * The workspace's app install, as the page knows it: `undefined` while the
 * page is still loading it, `null` when there is none, otherwise its id. A
 * tracker that signs in through the install (`credentialSource`) reads it
 * from here instead of loading it again, since the page owns the install
 * record.
 */
export type AppInstallState = { installationId: number } | null | undefined

/**
 * The parts of the workspace's app install that the page owns, for the
 * tracker that signs in through it. With this, that tracker's card is the
 * one card for the supplier: install, issues, and the apps using it.
 */
export interface InstallSlot {
  /** The install's repositories, or its "no longer installed" state. */
  repositories: ReactNode
  /** Which apps in the workspace use the install. */
  usedBy: ReactNode
  /** The install's own menu items (re-authorize and so on), placed first
   * in the card's menu. */
  menuItems: ProviderBlockMenuItem[]
  /** A second line under the account, such as "Organization · Installation #1". */
  installLine: string
  /** What only the install can do, as a line in "What OttoTester can do". */
  installOnlyAbility: string
  /** The same thing as a noun phrase for running text. */
  installPurpose: string
  /** Remove the install and every connection signed in through it. Safe
   * to repeat. */
  disconnectAll: () => Promise<void>
  /** Send the browser to install (or reinstall) the app. */
  install: () => void
  /** What the supplier last said about the install: the page's one lookup.
   * The card reads the account, permissions and status from here and never
   * asks again itself, so nothing on the card can disagree about whether
   * the install is there. */
  lookup: InstallLookup
}

/** One lookup of the install. `none`: no install record. `gone`: the
 * supplier confirmed it was removed. `unknown`: the supplier couldn't be
 * asked, which says nothing either way. */
export interface InstallLookup {
  status: 'none' | 'loading' | 'present' | 'gone' | 'unknown'
  account: GitHubInstallationAccount | null
  /** The install's issues permission (`read`, `write`, `none`) when known. */
  issuesPermission: string | null
  /** Ask again, keeping what is shown until the answer comes. */
  refresh: () => void
}

/** Whether a tracker signs in with a stored secret (Jira: an API token). */
function hasSecretField(descriptor: IssueTrackerProviderDescriptor): boolean {
  return descriptor.credentialSchema.some((f) => f.secret)
}

/** Whether a connection (or, before there is one, the tracker's default)
 * signs in through the workspace's app install rather than with typed-in
 * fields. */
function signsInThroughInstall(
  descriptor: IssueTrackerProviderDescriptor | undefined,
  connection?: IssueTrackerConnection | null,
): boolean {
  return Boolean(descriptor?.credentialSource) && signInOf(descriptor, connection) === 'app'
}

/** Whether a connection signs in with a token typed in on a tracker that
 * could have used its app install instead. */
function signsInWithTypedToken(
  descriptor: IssueTrackerProviderDescriptor | undefined,
  connection: IssueTrackerConnection | null,
): boolean {
  return Boolean(descriptor && connection && offersSignInChoice(descriptor)) && signInOf(descriptor, connection) === 'token'
}

function providerConfigValue(connection: IssueTrackerConnection, key: string): string {
  const v = connection.providerConfig?.[key]
  return typeof v === 'string' ? v : ''
}

/** The install's account, saved on the connection when it was made. */
function accountOf(connection: IssueTrackerConnection): string {
  return providerConfigValue(connection, 'account')
}

// ── The cards ───────────────────────────────────────────────────────────────

export function IssueTrackerCard({
  workspaceId,
  appInstall,
  installSlot,
  reloadKey,
}: {
  /** Needed only to look up the app install's account and settings link. */
  workspaceId?: string
  /** The workspace's app install, for a tracker that signs in through it. */
  appInstall?: AppInstallState
  /** The install's own parts, which make that tracker's card the one card
   * for its supplier. */
  installSlot?: InstallSlot
  /** Changes when the page changed connections itself (removing a broken
   * install), so the card reloads them. */
  reloadKey?: number
} = {}) {
  const [connections, setConnections] = useState<IssueTrackerConnection[] | null>(null)
  const { descriptors: providers, failed: providersFailed, retry: retryProviders } = useTrackerDescriptorsState()
  const [loadError, setLoadError] = useState<string | null>(null)

  const reload = useCallback(async () => {
    try {
      setConnections(await issueTrackersAPI.list())
      setLoadError(null)
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load issue trackers')
    }
  }, [])

  useEffect(() => { void reload() }, [reload, reloadKey])

  // One card per described tracker, with its connection when it has one.
  // A connection whose tracker isn't described (the descriptions failed to
  // load) still gets a card, so it never reads as never set up.
  const cards = useMemo(() => {
    const out: Array<{ provider: string; descriptor?: IssueTrackerProviderDescriptor; connection: IssueTrackerConnection | null }> = []
    for (const d of providers ?? []) {
      out.push({ provider: d.provider, descriptor: d, connection: connections?.find((c) => c.provider === d.provider) ?? null })
    }
    for (const c of connections ?? []) {
      if (!out.some((card) => card.provider === c.provider)) out.push({ provider: c.provider, connection: c })
    }
    return out
  }, [providers, connections])

  // Before the first fetch resolves, show a bare skeleton — we don't yet know
  // the connection state or the trackers' names needed to title the shells.
  // Mirrors the GitHub/Slack cards' loading skeleton.
  if ((connections === null || (providers === undefined && !providersFailed)) && !loadError) {
    return <div className="rounded-lg border border-border h-16 animate-pulse bg-muted/30" aria-hidden="true" />
  }

  const sharedErrors = (
    <>
      {loadError && <p className="text-sm text-destructive" role="alert">{loadError}</p>}
      {providersFailed && (
        // Without the trackers' descriptions the card can't tell a connected
        // tracker's name or form apart, so it says so and offers a retry
        // instead of showing the connection as unknown.
        <p className="text-sm text-destructive" role="alert">
          Couldn’t load the issue-tracker details.{' '}
          <button type="button" onClick={retryProviders} className="font-medium underline">Try again</button>
        </p>
      )}
    </>
  )

  if (cards.length === 0) {
    return (
      <ProviderCardShell headingId="issue-tracker-card" providerName="Issue tracker" summary={null} open onOpenChange={() => {}}>
        {sharedErrors}
        {!providersFailed && !loadError && (
          <p className="text-sm text-muted-foreground">No issue-tracker providers are available.</p>
        )}
      </ProviderCardShell>
    )
  }

  return (
    <div className="space-y-6">
      {cards.map((card) => (
        <TrackerCard
          key={card.provider}
          provider={card.provider}
          descriptor={card.descriptor}
          connection={card.connection}
          sharedErrors={sharedErrors}
          workspaceId={workspaceId}
          appInstall={appInstall}
          installSlot={card.descriptor?.credentialSource ? installSlot : undefined}
          onChanged={() => void reload()}
        />
      ))}
    </div>
  )
}

/** One tracker's card: its connection, or the way to connect it. */
function TrackerCard({
  provider,
  descriptor,
  connection,
  sharedErrors,
  workspaceId,
  appInstall,
  installSlot,
  onChanged,
}: {
  provider: string
  /** Absent when the trackers' descriptions failed to load. */
  descriptor?: IssueTrackerProviderDescriptor
  connection: IssueTrackerConnection | null
  sharedErrors: ReactNode
  workspaceId?: string
  appInstall: AppInstallState
  /** Set for the tracker that signs in through the install, when the page
   * makes its card the supplier's one card. */
  installSlot?: InstallSlot
  onChanged: () => void
}) {
  const [actionError, setActionError] = useState<string | null>(null)
  const [connectOpen, setConnectOpen] = useState(false)
  // Expanded while not connected (the call to action), collapsed once
  // connected (the details are reference) — matching the GitHub/Slack cards.
  // Once the operator toggles, their choice wins, so connecting or
  // disconnecting mid-session doesn't snap the card they're looking at.
  // The card mounts once the connections have loaded, so this first value
  // is the real one.
  const [open, setOpen] = useState<boolean>(() => !connection)
  const connected = Boolean(connection && descriptor)

  const name = installSlot && descriptor
    ? descriptor.displayName
    : connectionNameOf(descriptor ?? descriptorFor(undefined, provider))
  const viaInstall = signsInThroughInstall(descriptor, connection)
  const viaToken = signsInWithTypedToken(descriptor, connection)
  const lookup = installSlot?.lookup
  // Connected, but the install it reads through is gone: sync is paused.
  // Gone means no install record, or the supplier confirming it was removed.
  const installGone = Boolean(connection) && viaInstall && (appInstall === null || lookup?.status === 'gone')
  const appName = `${descriptor?.displayName ?? name} App`

  // The install's account as it is now. The connection saved the account it
  // was made on, but every read signs in through the current install, so a
  // CI reconnect to another account changes what is read: show that one.
  // Best effort: the saved account stands in until this loads, or if it fails.
  const installationId = appInstall?.installationId
  const [liveAccount, setLiveAccount] = useState<string | null>(null)
  // The install's issue permission as it is now. Read-only means failed
  // tests can't open issues until an org owner approves "Read and write".
  const [liveIssuesPermission, setLiveIssuesPermission] = useState<string | null>(null)
  const [liveInstallAccount, setLiveInstallAccount] = useState<GitHubInstallationAccount | null>(null)
  // The details call failed, so the install's permissions are not known.
  const [detailsFailed, setDetailsFailed] = useState(false)
  // Re-read the install's details once a minute while the App can't write
  // issues, so the message clears soon after an org owner approves the
  // change (GitHub's answer is cached on the server for five minutes).
  const [detailsCheck, setDetailsCheck] = useState(0)
  // On the supplier's one card the page's lookup is the only one.
  const issuesPermission = lookup
    ? (lookup.status === 'present' ? (lookup.issuesPermission ?? 'none') : null)
    : liveIssuesPermission
  const installAccountNow = lookup ? lookup.account : liveInstallAccount
  const lookupFailed = lookup ? lookup.status === 'unknown' : detailsFailed
  const waitingForWrite = issuesPermission !== null && issuesPermission !== 'write'
  const refreshLookup = lookup?.refresh
  useEffect(() => {
    if (!waitingForWrite) return
    const timer = setInterval(() => (refreshLookup ? refreshLookup() : setDetailsCheck((n) => n + 1)), 60_000)
    return () => clearInterval(timer)
  }, [waitingForWrite, refreshLookup])
  const wantsDetails = !installSlot && viaInstall && Boolean(connection)
  useEffect(() => {
    if (!wantsDetails || !workspaceId || !installationId) return
    let cancelled = false
    githubInstallationAPI
      .getDetails(workspaceId, installationId)
      .then((d) => {
        if (cancelled) return
        setLiveAccount(d.account?.login ?? null)
        setLiveIssuesPermission(d.permissions ? (d.permissions.issues ?? 'none') : null)
        setLiveInstallAccount(d.account ?? null)
        setDetailsFailed(false)
      })
      .catch(() => { if (!cancelled) setDetailsFailed(true) })
    return () => { cancelled = true }
  }, [wantsDetails, workspaceId, installationId, detailsCheck])
  // Once the install is gone the saved account is the one it "was using".
  // The live install account applies only to a connection that reads
  // through the install. A token's owner is the one GitHub named when it
  // was saved, even when an install is also loaded on this page.
  const liveAccountNow = lookup ? (lookup.account?.login ?? null) : liveAccount
  const account = connection ? ((viaInstall && !installGone && liveAccountNow) || accountOf(connection)) : ''
  // Only a connection that signs in through the App: a token's own rights
  // are reported in the run's log when a write is refused.
  const cannotWriteIssuesUrl = viaInstall && !installGone && installationId
    && issuesPermission !== null && issuesPermission !== 'write'
    ? buildManageOnGitHubUrl(installationId, installAccountNow)
    : undefined

  // Collapsed-header summary mirrors the Slack card's "Connected · <team>":
  // where it reads from (the site, or the install's account), so the
  // operator sees what is wired up without expanding. From the connection
  // alone, so a failed description load still reads as connected.
  const where = connection
    ? providerConfigValue(connection, 'baseUrl').replace(/^https?:\/\//, '') || account
    : ''
  const installAccount = installAccountNow?.login ?? ''
  const summary = installGone ? (
    <span className="text-amber-700 dark:text-amber-300">{appName} not installed</span>
  ) : viaToken ? (
    <span className="text-emerald-700 dark:text-emerald-300">Connected{account ? ` · token from ${account}` : ' · personal token'}</span>
  ) : connection && installSlot && viaInstall ? (
    <span className="text-emerald-700 dark:text-emerald-300">Connected{account ? ` · ${appName} on ${account}` : ''}</span>
  ) : connection ? (
    <span className="text-emerald-700 dark:text-emerald-300">Connected{where ? ` · ${where}` : ''}</span>
  ) : installSlot && appInstall ? (
    <span className="text-amber-700 dark:text-amber-300">Not finished{installAccount ? ` · ${appName} on ${installAccount}` : ''}</span>
  ) : (
    <span className="text-muted-foreground">Not connected</span>
  )

  return (
    <ProviderCardShell
      headingId={`issue-tracker-card-${provider}`}
      providerName={name}
      summary={summary}
      open={open}
      onOpenChange={setOpen}
    >
      {sharedErrors}
      {actionError && <p className="text-sm text-destructive" role="alert">{actionError}</p>}

      {connected ? (
        <ConnectedTrackerBlock
          connection={connection!}
          descriptor={descriptor!}
          account={account}
          installGone={installGone}
          appInstall={appInstall}
          cannotWriteIssuesUrl={cannotWriteIssuesUrl}
          writeCheck={writeCheckOf(issuesPermission, lookupFailed)}
          installSlot={installSlot}
          onChanged={onChanged}
          onError={setActionError}
        />
      ) : descriptor && installSlot && appInstall ? (
        <FinishConnectingBlock
          descriptor={descriptor}
          workspaceId={workspaceId}
          appInstall={appInstall}
          account={installAccount}
          installSlot={installSlot}
          onConnected={() => { setActionError(null); onChanged() }}
          onChanged={onChanged}
          onError={setActionError}
        />
      ) : descriptor && offersSignInChoice(descriptor) ? (
        <SignInChoiceCard
          descriptor={descriptor}
          workspaceId={workspaceId}
          appInstall={appInstall}
          installSlot={installSlot}
          onConnected={() => { setActionError(null); onChanged() }}
          onError={setActionError}
        />
      ) : descriptor && viaInstall ? (
        <InstallEmptyCard
          descriptor={descriptor}
          workspaceId={workspaceId}
          appInstall={appInstall}
          onConnected={() => { setActionError(null); onChanged() }}
          onError={setActionError}
        />
      ) : descriptor ? (
        <>
          <TrackerEmptyCard
            descriptor={descriptor}
            onConnect={() => { setActionError(null); setConnectOpen(true) }}
          />
          <CredentialsDialog
            open={connectOpen}
            mode="connect"
            descriptor={descriptor}
            onClose={() => setConnectOpen(false)}
            onSaved={() => { setConnectOpen(false); setActionError(null); onChanged() }}
            onError={setActionError}
          />
        </>
      ) : null}
    </ProviderCardShell>
  )
}

// ── Not-connected: the centered CTA ──────────────────────────────────────────

/** The connect button in the tracker's colour. */
function ConnectButton({
  descriptor, onClick, disabled = false, busy = false, label,
}: {
  descriptor: IssueTrackerProviderDescriptor
  onClick: () => void
  disabled?: boolean
  busy?: boolean
  /** In place of "Connect <tracker>". */
  label?: string
}) {
  const name = connectionNameOf(descriptor)
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || busy}
      className={`mt-3 inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-semibold text-white transition-colors disabled:opacity-45 disabled:cursor-not-allowed ${trackerButtonClass(descriptor.logoKey)}`}
    >
      <TrackerLogo logoKey={descriptor.logoKey} name={descriptor.displayName} className="h-3.5 w-3.5" mono />
      {busy ? 'Checking…' : (label ?? `Connect ${name}`)}
    </button>
  )
}

/** Empty state — a centered call-to-action matching the GitHub/Slack empty
 * cards on this page (logo + one line + a single Connect button). Unlike those
 * two (which redirect to OAuth), a tracker that signs in with fields needs
 * them typed in, so the button opens the credentials dialog rather than
 * navigating away. */
function TrackerEmptyCard({ descriptor, onConnect }: { descriptor: IssueTrackerProviderDescriptor; onConnect: () => void }) {
  const label = connectionNameOf(descriptor)
  const words = trackerWords(descriptor)
  return (
    <div className="text-center py-2">
      <TrackerLogo logoKey={descriptor.logoKey} name={label} className="mx-auto mb-3 h-10 w-10" />
      <h3 className="text-sm font-semibold">Connect {label} to pull issues into coverage</h3>
      <p className="mt-1 text-xs text-muted-foreground max-w-md mx-auto leading-relaxed">
        Store your {label} credentials once here at the workspace. Each app then picks which
        {' '}{words.projects} to pull from — no per-app token.
      </p>
      <ConnectButton descriptor={descriptor} onClick={onConnect} />
    </div>
  )
}

/**
 * Empty state for a tracker that signs in through the workspace's app
 * install (mockup A, states 1–3). There is nothing to type, so Connect checks
 * the install can read issues and saves straight away, with no dialog.
 *
 *   1. Installed → "Uses the GitHub App installed on <account>".
 *   2. Not installed → says the install comes first; Connect is disabled.
 *   3. Installed but can't read issues → after Connect, a banner links to
 *      the install's settings page on GitHub, the same link the GitHub
 *      card's "View on GitHub" uses.
 */
function InstallEmptyCard({
  descriptor,
  workspaceId,
  appInstall,
  onConnected,
  onError,
  bare = false,
  connectLabel,
  lookup,
}: {
  descriptor: IssueTrackerProviderDescriptor
  workspaceId?: string
  appInstall: AppInstallState
  onConnected: () => void
  onError: (msg: string) => void
  /** Inside the sign-in choice, which already shows the logo and heading. */
  bare?: boolean
  /** In place of "Connect <tracker>". */
  connectLabel?: string
  /** The page's one lookup of the install, when there is one; then this
   * card never asks again itself. */
  lookup?: InstallLookup
}) {
  const name = connectionNameOf(descriptor)
  const appName = `${descriptor.displayName} App`
  const words = trackerWords(descriptor)
  const installationId = appInstall?.installationId
  const [ownAccount, setAccount] = useState<GitHubInstallationAccount | null>(null)
  const account = lookup ? lookup.account : ownAccount
  const [connecting, setConnecting] = useState(false)
  const [refusal, setRefusal] = useState<Extract<IssueTrackerValidationResult, { ok: false }> | null>(null)

  // The install's account, for "installed on <account>" and the link to its
  // settings on GitHub. Best effort: without it the card still connects.
  const hasLookup = Boolean(lookup)
  useEffect(() => {
    if (hasLookup || !workspaceId || !installationId) return
    let cancelled = false
    githubInstallationAPI
      .getDetails(workspaceId, installationId)
      .then((d) => { if (!cancelled) setAccount(d.account) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [hasLookup, workspaceId, installationId])

  async function handleConnect() {
    setConnecting(true)
    setRefusal(null)
    try {
      const outcome = await issueTrackersAPI.connect({
        provider: descriptor.provider,
        ...(offersSignInChoice(descriptor) ? { signIn: 'app' as const } : {}),
        credentials: {},
      })
      if (outcome.ok) onConnected()
      else setRefusal(outcome.validation)
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to connect the issue tracker')
    } finally {
      setConnecting(false)
    }
  }

  if (appInstall === null) {
    return (
      <div className="text-center py-2">
        <TrackerLogo logoKey={descriptor.logoKey} name={descriptor.displayName} className="mx-auto mb-3 h-10 w-10" />
        <h3 className="text-sm font-semibold">Install the {appName} first</h3>
        <p className="mt-1 text-xs text-muted-foreground max-w-md mx-auto leading-relaxed">
          {name} uses the same {appName} as test runs. Install the {appName} on this workspace first,
          then come back here.
        </p>
        <ConnectButton descriptor={descriptor} onClick={() => {}} disabled />
      </div>
    )
  }

  const accountName = account?.login
  const manageUrl = installationId ? buildManageOnGitHubUrl(installationId, account) : undefined
  return (
    <div>
      <div className={bare ? '' : 'text-center py-2'}>
        {!bare && (
          <>
            <TrackerLogo logoKey={descriptor.logoKey} name={descriptor.displayName} className="mx-auto mb-3 h-10 w-10" />
            <h3 className="text-sm font-semibold">Connect {name} to pull issues into coverage</h3>
          </>
        )}
        {refusal?.reason !== 'missing_permission' && (
          <p className="mt-1 text-xs text-muted-foreground max-w-md mx-auto leading-relaxed">
            Uses the {appName} installed on{' '}
            {accountName ? <strong className="text-foreground">{accountName}</strong> : 'this workspace'}.
            {' '}There’s no token to store.{bare ? '' : ` Each app then picks which ${words.projects} to pull from.`}
          </p>
        )}
        <ConnectButton descriptor={descriptor} onClick={() => void handleConnect()} busy={connecting} label={connectLabel} />
      </div>
      {refusal && (
        <div role="alert" className="mt-3 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          {refusal.reason === 'missing_permission' ? (
            <>
              The {appName} on {accountName ?? 'this workspace'} doesn’t have permission to read issues.{' '}
              {manageUrl ? (
                <a href={manageUrl} target="_blank" rel="noreferrer" className="font-medium underline">Approve the new permission on {descriptor.displayName}</a>
              ) : (
                `Approve the new permission on ${descriptor.displayName}`
              )}
              , then try again.
            </>
          ) : (
            refusal.message
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Not connected, on a tracker that offers both ways in (mockup Option B):
 * two choices, the app install picked and marked recommended, each with its
 * trade-off in one line, so the cost of a token is read before it is picked.
 *
 *   - App install, installed → the install's own Connect, as before.
 *   - App install, not installed → "Install the GitHub App". Coming back
 *     from the install connects the tracker on the server.
 *   - Personal token → a warning, the token field, and Connect.
 * With an `installSlot` this is the supplier's one card, so it says what the
 * connection is for, including what only the install can do.
 */
function SignInChoiceCard({
  descriptor,
  workspaceId,
  appInstall,
  installSlot,
  onConnected,
  onError,
}: {
  descriptor: IssueTrackerProviderDescriptor
  workspaceId?: string
  appInstall: AppInstallState
  installSlot?: InstallSlot
  onConnected: () => void
  onError: (msg: string) => void
}) {
  const [choice, setChoice] = useState<'app' | 'token'>('app')
  const name = connectionNameOf(descriptor)
  const appName = `${descriptor.displayName} App`
  const words = trackerWords(descriptor)
  const tokenLabel = descriptor.credentialSchema.find((f) => f.secret)?.label ?? 'Personal token'
  const options: Array<{ value: 'app' | 'token'; title: string; recommended?: boolean; detail: string }> = [
    {
      value: 'app',
      title: appName,
      recommended: true,
      detail: 'Works for the whole team and isn’t tied to one person. Your org owner picks the repositories. Needs an owner to approve the install.',
    },
    {
      value: 'token',
      title: tokenLabel,
      detail: `Use this if you can’t get the App approved. It’s tied to you, and it stops working if you leave or the token expires.${installSlot ? ` Only the App can ${lowerFirst(installSlot.installOnlyAbility)}.` : ''}`,
    },
  ]
  return (
    <div className="py-2 space-y-3">
      <div className="text-center">
        <TrackerLogo logoKey={descriptor.logoKey} name={descriptor.displayName} className="mx-auto mb-3 h-10 w-10" />
        {installSlot ? (
          <>
            <h3 className="text-sm font-semibold">Connect OttoTester to {descriptor.displayName}</h3>
            <p className="mt-1 text-xs text-muted-foreground max-w-md mx-auto leading-relaxed">
              Used for {installSlot.installPurpose}, and for reading and opening issues. Each app then picks
              its {words.projects} in its Integrations section on the Apps page. How should OttoTester sign in?
            </p>
          </>
        ) : (
          <>
            <h3 className="text-sm font-semibold">Connect {name} to pull issues into coverage</h3>
            <p className="mt-1 text-xs text-muted-foreground max-w-md mx-auto leading-relaxed">
              Each app then picks which {words.projects} to pull from. How should OttoTester sign in?
            </p>
          </>
        )}
      </div>
      <div role="radiogroup" aria-label={`How ${name} signs in`} className="space-y-2">
        {options.map((o) => {
          const on = choice === o.value
          return (
            <label
              key={o.value}
              className={`flex gap-2.5 rounded-lg border px-3 py-2.5 cursor-pointer ${on ? 'border-primary bg-primary/5' : 'border-border'}`}
            >
              <input
                type="radio"
                name={`sign-in-${descriptor.provider}`}
                value={o.value}
                checked={on}
                onChange={() => setChoice(o.value)}
                className="mt-0.5 accent-primary"
              />
              <span className="min-w-0">
                <span className="flex flex-wrap items-center gap-2 text-sm font-semibold">
                  {o.title}
                  {o.recommended && (
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary">Recommended</span>
                  )}
                </span>
                <span className="block text-xs text-muted-foreground">{o.detail}</span>
              </span>
            </label>
          )
        })}
      </div>
      {choice === 'app' ? (
        appInstall === null ? (
          <div>
            <button
              type="button"
              disabled={!workspaceId || !installSlot}
              onClick={() => installSlot?.install()}
              className={`mt-2 inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-semibold text-white transition-colors disabled:opacity-45 disabled:cursor-not-allowed ${trackerButtonClass(descriptor.logoKey)}`}
            >
              <TrackerLogo logoKey={descriptor.logoKey} name={descriptor.displayName} className="h-3.5 w-3.5" mono />
              Install the {appName}
            </button>
          </div>
        ) : (
          <InstallEmptyCard
            bare
            descriptor={descriptor}
            workspaceId={workspaceId}
            appInstall={appInstall}
            onConnected={onConnected}
            onError={onError}
          />
        )
      ) : (
        <TokenConnectForm descriptor={descriptor} installSlot={installSlot} onConnected={onConnected} onError={onError} />
      )}
    </div>
  )
}

/** "Post checks on pull requests" → "post checks on pull requests". */
function lowerFirst(s: string): string {
  return s.charAt(0).toLowerCase() + s.slice(1)
}

/** The personal-token half of the sign-in choice: what a token costs, the
 * field, and Connect. The server checks the token can read issues before
 * saving, and works out whose it is. */
function TokenConnectForm({
  descriptor,
  installSlot,
  onConnected,
  onError,
}: {
  descriptor: IssueTrackerProviderDescriptor
  installSlot?: InstallSlot
  onConnected: () => void
  onError: (msg: string) => void
}) {
  const field = descriptor.credentialSchema.find((f) => f.secret) ?? descriptor.credentialSchema[0]!
  const [value, setValue] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [refusal, setRefusal] = useState<Extract<IssueTrackerValidationResult, { ok: false }> | null>(null)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    const token = value.trim()
    if (!token) return
    setSubmitting(true)
    setRefusal(null)
    try {
      const outcome = await issueTrackersAPI.connect({ provider: descriptor.provider, signIn: 'token', credentials: { [field.key]: token } })
      if (outcome.ok) onConnected()
      else setRefusal(outcome.validation)
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to connect the issue tracker')
    } finally {
      setSubmitting(false)
    }
  }

  const id = `it-token-${descriptor.provider}`
  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="space-y-2">
      <div role="note" className="rounded-md bg-amber-50 px-2.5 py-2 text-xs text-amber-800 dark:bg-amber-950 dark:text-amber-300">
        A token belongs to one person. It stops working if they leave or the token expires.
        {installSlot ? ` Only the ${descriptor.displayName} App can ${lowerFirst(installSlot.installOnlyAbility)}.` : ''}
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={id}>{field.label}</Label>
        <Input
          id={id}
          type="password"
          autoComplete="new-password"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled={submitting}
        />
        <p className="text-[11px] text-muted-foreground">
          Make a fine-grained token on {descriptor.displayName} with <strong>Issues: read and write</strong> access on the repositories you
          want, or a classic token with the <strong>repo</strong> scope. Some orgs need an owner to approve it.
        </p>
      </div>
      {refusal && <ValidationResultBanner result={refusal} />}
      <button
        type="submit"
        disabled={submitting || value.trim().length === 0}
        className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-semibold text-white transition-colors disabled:opacity-45 disabled:cursor-not-allowed ${trackerButtonClass(descriptor.logoKey)}`}
      >
        {submitting ? 'Checking…' : 'Connect with this token'}
      </button>
    </form>
  )
}

// ── Typed result banner ─────────────────────────────────────────────────────

/** Renders the typed stage-1 result of a re-check. */
function ValidationResultBanner({
  result,
  viaInstall = false,
}: {
  result: IssueTrackerValidationResult
  /** The tracker signs in through an app install, so there are no
   * credentials to speak of. */
  viaInstall?: boolean
}) {
  if (result.ok) {
    return (
      <div className="flex items-center gap-2 text-sm rounded-lg border border-green-300 bg-green-50 text-green-800 dark:border-green-900 dark:bg-green-950 dark:text-green-300 px-3 py-2">
        <CheckCircle2 className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
        <span>{viaInstall ? 'Connected — OttoTester can read issues.' : 'Connected — the credentials work.'}</span>
      </div>
    )
  }
  return (
    <div
      role="alert"
      className="flex items-start gap-2 text-sm rounded-lg border border-destructive/50 bg-destructive/10 text-destructive px-3 py-2"
    >
      <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
      <span>{result.message}</span>
    </div>
  )
}

// ── Credential fields (descriptor-driven) ───────────────────────────────────

/** Renders one input per credential-schema field. Secret fields use a
 * password input; `placeholders` overrides the placeholder per field (used
 * by the update dialog's "leave blank to keep" hint). */
function CredentialFields({
  fields,
  values,
  onChange,
  disabled,
  placeholders,
  idPrefix,
}: {
  fields: IssueTrackerCredentialField[]
  values: Record<string, string>
  onChange: (key: string, value: string) => void
  disabled: boolean
  placeholders?: Record<string, string>
  idPrefix: string
}) {
  return (
    <div className="space-y-3">
      {fields.map((field) => {
        const id = `${idPrefix}-${field.key}`
        return (
          <div key={field.key} className="space-y-1.5">
            <Label htmlFor={id}>{field.label}</Label>
            <Input
              id={id}
              type={field.secret ? 'password' : 'text'}
              autoComplete={field.secret ? 'new-password' : 'off'}
              value={values[field.key] ?? ''}
              placeholder={placeholders?.[field.key]}
              onChange={(e) => onChange(field.key, e.target.value)}
              disabled={disabled}
            />
          </div>
        )
      })}
    </div>
  )
}

// ── Connected: the provider block + actions ─────────────────────────────────

/** "1 repository" / "14 repositories". */
function countOf(n: number, words: { project: string; projects: string }): string {
  return `${n} ${n === 1 ? words.project : words.projects}`
}

function ConnectedTrackerBlock({
  connection,
  descriptor,
  account,
  installGone,
  appInstall,
  cannotWriteIssuesUrl,
  writeCheck,
  installSlot,
  onChanged,
  onError,
}: {
  connection: IssueTrackerConnection
  descriptor: IssueTrackerProviderDescriptor
  /** The account it reads through: the install's current one when known. */
  account: string
  /** Signs in through an app install that has since been removed. */
  installGone: boolean
  /** The workspace's app install, for switching a token connection to it. */
  appInstall: AppInstallState
  /** Set while the App can read issues but not write them: where to
   * approve the change on GitHub. */
  cannotWriteIssuesUrl?: string
  /** What is known about the install's permission to write issues. */
  writeCheck: WriteCheck
  /** The install's own parts, when this is the supplier's one card. */
  installSlot?: InstallSlot
  onChanged: () => void
  onError: (msg: string) => void
}) {
  const tz = useUserTimezone()
  const [updateOpen, setUpdateOpen] = useState(false)
  const [disconnectOpen, setDisconnectOpen] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<IssueTrackerValidationResult | null>(null)
  const [projectCount, setProjectCount] = useState<number | null>(null)
  const [switching, setSwitching] = useState(false)
  const [switchRefusal, setSwitchRefusal] = useState<Extract<IssueTrackerValidationResult, { ok: false }> | null>(null)
  // "Read issues" on the supplier's one card is a check, not a guess from
  // the connection being saved: the same validation as Test connection,
  // run when the card opens and again on every Test.
  const [readCheck, setReadCheck] = useState<Ability>('checking')

  const name = installSlot ? descriptor.displayName : connectionNameOf(descriptor)
  const viaInstall = signsInThroughInstall(descriptor, connection)
  const viaToken = signsInWithTypedToken(descriptor, connection)
  const appName = `${descriptor.displayName} App`
  const words = trackerWords(descriptor)
  const baseUrl = providerConfigValue(connection, 'baseUrl')
  // On the supplier's one card, the App's Disconnect removes the install
  // too. A token connection's removes only the token.
  const disconnectsInstall = Boolean(installSlot) && viaInstall
  // A non-secret identity field to show under the base URL, if the
  // provider has one (Jira: email). First non-secret, non-baseUrl field.
  const identityField = descriptor.credentialSchema.find(
    (f) => !f.secret && f.key !== 'baseUrl',
  )
  const identityValue = identityField ? providerConfigValue(connection, identityField.key) : ''

  // An install or a personal token has no site to show, so the card says
  // how much it can read instead. Best effort: the line is left off if this
  // fails.
  const countsProjects = (viaInstall && !installGone) || viaToken
  // Asked again whenever the connection is saved (a switch to the install,
  // a new token), since each can see different repositories. The old count
  // is cleared first so it never shows against the new sign-in.
  useEffect(() => {
    setProjectCount(null)
    if (!countsProjects) return
    let cancelled = false
    issueTrackersAPI
      .listProjects(connection.id)
      .then((p) => { if (!cancelled) setProjectCount(p.length) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [countsProjects, connection.id, connection.signIn, connection.updatedAt])

  // The one way the read check runs, on load and on Test: it says checking
  // until the answer, and not checked if the request fails. Only the latest
  // request may set the line, so a slow earlier answer can't overwrite it.
  const readRequest = useRef(0)
  const checkRead = useCallback(async (): Promise<IssueTrackerValidationResult> => {
    const mine = ++readRequest.current
    setReadCheck('checking')
    try {
      const result = await issueTrackersAPI.validate(connection.id)
      if (readRequest.current === mine) setReadCheck(abilityOfValidation(result))
      return result
    } catch (err) {
      if (readRequest.current === mine) setReadCheck('unchecked')
      throw err
    }
  }, [connection.id])
  const checksOnLoad = Boolean(installSlot)
  useEffect(() => {
    if (!checksOnLoad) return
    checkRead().catch(() => {})
    return () => { readRequest.current++ }
  }, [checksOnLoad, checkRead, connection.signIn, connection.updatedAt])

  async function handleSwitchToApp() {
    setSwitching(true)
    setSwitchRefusal(null)
    try {
      const outcome = await issueTrackersAPI.switchToApp(connection.id)
      if (outcome.ok) onChanged()
      else setSwitchRefusal(outcome.validation)
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to switch to the app install')
    } finally {
      setSwitching(false)
    }
  }

  async function handleTest() {
    setTesting(true)
    setTestResult(null)
    try {
      setTestResult(await checkRead())
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to check the connection')
    } finally {
      setTesting(false)
    }
  }

  async function handleDisconnect() {
    setDisconnecting(true)
    try {
      if (disconnectsInstall) await installSlot!.disconnectAll()
      else await issueTrackersAPI.delete(connection.id)
      setDisconnectOpen(false)
      onChanged()
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to disconnect the issue tracker')
    } finally {
      setDisconnecting(false)
    }
  }

  const menuItems = [
    ...(disconnectsInstall ? installSlot!.menuItems : []),
    {
      key: 'test',
      label: testing ? 'Checking…' : 'Test connection',
      icon: <RefreshCw className="h-3.5 w-3.5" />,
      disabled: testing,
      onClick: () => void handleTest(),
    },
    // A connection through an app install has no credentials to update.
    ...(descriptor.credentialSchema.length > 0 && !viaInstall
      ? [{
          key: 'update',
          label: viaToken ? 'Replace token' : 'Update credentials',
          icon: <KeyRound className="h-3.5 w-3.5" />,
          onClick: () => setUpdateOpen(true),
        }]
      : []),
    {
      key: 'disconnect',
      label: 'Disconnect',
      icon: <Trash2 className="h-3.5 w-3.5" />,
      danger: true,
      separator: true,
      onClick: () => setDisconnectOpen(true),
    },
  ]

  const subtitle = viaInstall && installSlot && !installGone
    ? `${appName} on ${account || 'this workspace'}${installSlot.installLine ? ` · ${installSlot.installLine}` : ''}`
    : viaInstall
    ? `${installGone ? 'Was using' : 'Through'} the ${appName} on ${account || 'this workspace'}`
    : viaToken
      ? `Personal token from ${account || 'an unknown account'}`
      : baseUrl || 'connection'
  const statusBadge = installGone ? (
    <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-900 whitespace-nowrap">
      App not installed
    </span>
  ) : (
    <ConnectedBadge />
  )

  return (
    <>
      <ProviderBlock
        mark={<TrackerLogo logoKey={descriptor.logoKey} name={descriptor.displayName} className="h-8 w-8" />}
        name={name}
        subtitle={subtitle}
        statusBadge={statusBadge}
        menuItems={menuItems}
      >
        {installGone ? (
          <div role="status" className="rounded-md border border-amber-300 bg-amber-50 px-2.5 py-2 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300">
            The {appName} was removed from {account || 'this workspace'}, so sync is paused.{' '}
            {installSlot ? (
              <button type="button" onClick={installSlot.install} className="font-medium underline">Install it again</button>
            ) : (
              <>Install it again on this workspace</>
            )}
            . Nothing else needs to change; the next sync picks it up.
          </div>
        ) : (
          <div className="text-xs text-muted-foreground space-y-0.5">
            {identityValue && <div>Signed in as {identityValue}</div>}
            {countsProjects && projectCount !== null && <div>Can read issues in {countOf(projectCount, words)}</div>}
            <div>Last checked: {fmtDate(connection.lastValidatedAt, tz)}</div>
            <div>Connected: {fmtDate(connection.createdAt, tz)}</div>
          </div>
        )}
        {viaToken && (
          <div className="mt-3 space-y-2">
            <div role="note" className="rounded-md bg-amber-50 px-2.5 py-2 text-xs text-amber-800 dark:bg-amber-950 dark:text-amber-300">
              If {account || 'the token’s owner'} leaves or the token expires, OttoTester stops reading issues. Switching to
              the {appName} avoids this.
            </div>
            {appInstall === null ? (
              installSlot ? (
                <Button size="sm" variant="outline" onClick={installSlot.install}>
                  Install the {appName}
                </Button>
              ) : (
                <p className="text-xs text-muted-foreground">To switch, install the {appName} on this workspace first.</p>
              )
            ) : (
              <Button size="sm" variant="outline" onClick={() => void handleSwitchToApp()} disabled={switching || appInstall === undefined}>
                {switching ? 'Checking…' : `Switch to the ${appName}`}
              </Button>
            )}
            {switchRefusal && <ValidationResultBanner result={switchRefusal} />}
          </div>
        )}
        {cannotWriteIssuesUrl && (
          <div role="status" className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-2 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300">
            The {appName} needs permission to write issues, so failed tests can't open issues yet.{' '}
            <a href={cannotWriteIssuesUrl} target="_blank" rel="noreferrer" className="font-medium underline">Approve the new permission on {descriptor.displayName}</a>.
          </div>
        )}
        {testResult && (
          <div className="mt-3">
            <ValidationResultBanner result={testResult} viaInstall={viaInstall} />
          </div>
        )}
        {installSlot && (
          <div className="mt-3 space-y-3">
            {viaInstall && installSlot.repositories}
            <AbilityList
              installSlot={installSlot}
              supplierName={descriptor.displayName}
              appName={appName}
              installAccount={installSlot.lookup.account?.login ?? ''}
              {...abilitiesOf({ appInstall, lookup: installSlot.lookup, installGone, viaToken, writeCheck, readCheck })}
              tokenSignIn={viaToken}
            />
            {installSlot.usedBy}
          </div>
        )}
      </ProviderBlock>

      {descriptor.credentialSchema.length > 0 && !viaInstall && (
        <CredentialsDialog
          open={updateOpen}
          mode="update"
          connection={connection}
          descriptor={descriptor}
          onClose={() => setUpdateOpen(false)}
          onSaved={() => { setUpdateOpen(false); onChanged() }}
          onError={onError}
        />
      )}

      <ConfirmDialog
        open={disconnectOpen}
        onClose={() => setDisconnectOpen(false)}
        title={`Disconnect ${name}?`}
        description={disconnectsInstall ? (
          <>
            OttoTester stops {installSlot!.installPurpose} and stops reading and opening issues. The {appName} stays
            installed on {descriptor.displayName}. Apps that use it will show as disconnected until you connect again.
          </>
        ) : (
          <>
            OttoTester will stop reading issues from{' '}
            <strong>{baseUrl || account || 'this tracker'}</strong>.
            {hasSecretField(descriptor) && !viaInstall ? ' The stored token is deleted.' : ''}
            {viaInstall ? ` The ${appName} stays installed.` : ''} Apps that
            pull from it will show as disconnected until you connect again.
          </>
        )}
        confirmLabel="Disconnect"
        confirmVariant="destructive"
        onConfirm={handleDisconnect}
        busy={disconnecting}
      />
    </>
  )
}

/** One line of "What OttoTester can do": available, not available, still
 * being asked, or not checked at all. */
type Ability = 'yes' | 'no' | 'checking' | 'unchecked'
const ABILITY_MARK: Record<Ability, string> = { yes: '✓', no: '–', checking: '…', unchecked: '?' }
const ABILITY_LABEL: Record<Ability, string> = {
  yes: 'Available: ',
  no: 'Not available: ',
  checking: 'Checking: ',
  unchecked: 'Not checked: ',
}

/** What is known about the install's permission to write issues. Kept
 * apart from "not allowed": a lookup still running, or one that failed,
 * says nothing about the permission. */
type WriteCheck = 'allowed' | 'missing' | 'checking' | 'unknown'

function writeCheckOf(permission: string | null, lookupFailed: boolean): WriteCheck {
  // A failed lookup comes first: an earlier answer is no longer current.
  if (lookupFailed) return 'unknown'
  if (permission === 'write') return 'allowed'
  if (permission !== null) return 'missing'
  return 'checking'
}

/** A validation result as a line: refused sign-in or permission is not
 * available; a supplier that couldn't be reached is not checked. */
function abilityOfValidation(result: IssueTrackerValidationResult): Ability {
  if (result.ok) return 'yes'
  return result.reason === 'unreachable' || result.reason === 'network' ? 'unchecked' : 'no'
}

function abilityOfWriteCheck(check: WriteCheck): Ability {
  return check === 'allowed' ? 'yes' : check === 'missing' ? 'no' : check === 'checking' ? 'checking' : 'unchecked'
}

/**
 * Whether what only the install can do is available: the one rule for every
 * view of the supplier's card, connected or not, so a view can never say
 * "available" beside the repository list's "no longer installed".
 */
function installAbility(appInstall: AppInstallState, lookup: InstallLookup): Ability {
  if (appInstall === undefined || lookup.status === 'loading') return 'checking'
  if (appInstall === null || lookup.status === 'gone' || lookup.status === 'none') return 'no'
  return lookup.status === 'unknown' ? 'unchecked' : 'yes'
}

/**
 * The three lines for a connected tracker on the supplier's one card.
 *
 * Availability means access that works now, not a saved connection. A
 * connection signed in through an install that is known to be gone reads and
 * opens nothing, so every line says not available. While the page is still
 * finding out whether the install is there, the lines that depend on it say
 * checking. A token reads without the install, and its write is never
 * checked: saving it proves only a read.
 */
function abilitiesOf({
  appInstall,
  lookup,
  installGone,
  viaToken,
  writeCheck,
  readCheck,
}: {
  appInstall: AppInstallState
  lookup: InstallLookup
  installGone: boolean
  viaToken: boolean
  writeCheck: WriteCheck
  /** The connection's own check: the validation run on load and on Test. */
  readCheck: Ability
}): { install: Ability; readsIssues: Ability; opensIssues: Ability } {
  const install = installAbility(appInstall, lookup)
  if (viaToken) return { install, readsIssues: readCheck, opensIssues: 'unchecked' }
  if (installGone) return { install: 'no', readsIssues: 'no', opensIssues: 'no' }
  if (install !== 'yes') return { install, readsIssues: install, opensIssues: install }
  // The install is there: reading also needs its issues permission.
  const readsIssues: Ability = lookup.issuesPermission === 'none' ? 'no' : readCheck
  return { install, readsIssues, opensIssues: abilityOfWriteCheck(writeCheck) }
}

/**
 * "What OttoTester can do" on the supplier's one card: one line per job,
 * each marked available or not, so a sign-in's limits are read before
 * anyone wonders why checks never appear. A missing permission keeps its
 * own approve link in the banner above.
 */
function AbilityList({
  installSlot,
  supplierName,
  appName,
  installAccount = '',
  install,
  readsIssues,
  opensIssues,
  tokenSignIn = false,
}: {
  installSlot: InstallSlot
  supplierName: string
  appName: string
  /** Who the install is on, named beside a token connection. */
  installAccount?: string
  /** Whether the install is there, whatever issues sign in with. */
  install: Ability
  readsIssues: Ability
  /** Writing issues is never assumed: a token is checked for reading only,
   * and the App's permission is known only once GitHub has answered. */
  opensIssues: Ability
  /** Issues sign in with a personal token. */
  tokenSignIn?: boolean
}) {
  const openText: Record<Ability, string> = {
    yes: 'Open issues when a test fails',
    no: 'Open issues when a test fails',
    checking: 'Open issues when a test fails: checking…',
    unchecked: tokenSignIn
      ? `Open issues when a test fails: not checked. ${supplierName} only confirms a token can read issues. If it can't write them, the run's log says so.`
      : `Open issues when a test fails: not checked, because ${supplierName} couldn’t be asked.`,
  }
  const lines: Array<{ key: string; state: Ability; text: string }> = [
    {
      key: 'install',
      state: install,
      text: install === 'no'
        ? `${installSlot.installOnlyAbility}. Only the ${appName} can do this.`
        : install === 'checking'
          ? `${installSlot.installOnlyAbility}: checking…`
          : install === 'unchecked'
            ? `${installSlot.installOnlyAbility}: not checked, because ${supplierName} couldn’t be asked.`
            : tokenSignIn && installAccount
              ? `${installSlot.installOnlyAbility} through the ${appName} on ${installAccount}`
              : installSlot.installOnlyAbility,
    },
    {
      key: 'read',
      state: readsIssues,
      text: readsIssues === 'checking'
        ? 'Read issues for coverage: checking…'
        : readsIssues === 'unchecked'
          ? `Read issues for coverage: not checked, because ${supplierName} couldn’t be asked.`
          : 'Read issues for coverage',
    },
    { key: 'open', state: opensIssues, text: openText[opensIssues] },
  ]
  return (
    <div className="rounded border border-border px-3 py-2">
      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground pb-1">What OttoTester can do</div>
      <ul className="space-y-0.5 text-xs" aria-label="What OttoTester can do">
        {lines.map((l) => (
          <li key={l.key} className="flex gap-2">
            <span aria-hidden="true" className={l.state === 'yes' ? 'font-bold text-emerald-700 dark:text-emerald-300' : 'font-bold text-muted-foreground'}>
              {ABILITY_MARK[l.state]}
            </span>
            <span className={l.state === 'yes' ? '' : 'text-muted-foreground'}>
              <span className="sr-only">{ABILITY_LABEL[l.state]}</span>
              {l.text}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * The supplier's one card when the app is installed but its issue
 * connection was never made: an install from before installs connected
 * everything, or one whose connect step failed on the way back. One click
 * finishes it. The install's menu and Disconnect are here too, since this
 * is the only place the install shows.
 */
function FinishConnectingBlock({
  descriptor,
  workspaceId,
  appInstall,
  account,
  installSlot,
  onConnected,
  onChanged,
  onError,
}: {
  descriptor: IssueTrackerProviderDescriptor
  workspaceId?: string
  appInstall: AppInstallState
  account: string
  installSlot: InstallSlot
  onConnected: () => void
  onChanged: () => void
  onError: (msg: string) => void
}) {
  const appName = `${descriptor.displayName} App`
  const [disconnectOpen, setDisconnectOpen] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)

  async function handleDisconnect() {
    setDisconnecting(true)
    try {
      await installSlot.disconnectAll()
      setDisconnectOpen(false)
      onChanged()
    } catch (err) {
      onError(err instanceof Error ? err.message : `Failed to disconnect ${descriptor.displayName}`)
    } finally {
      setDisconnecting(false)
    }
  }

  return (
    <>
      <ProviderBlock
        mark={<TrackerLogo logoKey={descriptor.logoKey} name={descriptor.displayName} className="h-8 w-8" />}
        name={descriptor.displayName}
        subtitle={`${appName} on ${account || 'this workspace'}${installSlot.installLine ? ` · ${installSlot.installLine}` : ''}`}
        statusBadge={
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-900 whitespace-nowrap">
            Not finished
          </span>
        }
        menuItems={[
          ...installSlot.menuItems,
          {
            key: 'disconnect',
            label: 'Disconnect',
            icon: <Trash2 className="h-3.5 w-3.5" />,
            danger: true,
            separator: true,
            onClick: () => setDisconnectOpen(true),
          },
        ]}
      >
        <div className="space-y-3">
          <InstallEmptyCard
            bare
            descriptor={descriptor}
            workspaceId={workspaceId}
            appInstall={appInstall}
            connectLabel="Finish connecting"
            lookup={installSlot.lookup}
            onConnected={onConnected}
            onError={onError}
          />
          {installSlot.repositories}
          <AbilityList
            installSlot={installSlot}
            supplierName={descriptor.displayName}
            appName={appName}
            install={installAbility(appInstall, installSlot.lookup)}
            readsIssues="no"
            opensIssues="no"
          />
          {installSlot.usedBy}
        </div>
      </ProviderBlock>
      <ConfirmDialog
        open={disconnectOpen}
        onClose={() => setDisconnectOpen(false)}
        title={`Disconnect ${descriptor.displayName}?`}
        description={`OttoTester stops ${installSlot.installPurpose}. The ${appName} stays installed on ${descriptor.displayName}.`}
        confirmLabel="Disconnect"
        confirmVariant="destructive"
        onConfirm={handleDisconnect}
        busy={disconnecting}
      />
    </>
  )
}

// ── Connect / update credentials dialog ─────────────────────────────────────

/**
 * One dialog for both connecting a new issue tracker and updating an existing
 * connection's credentials (#1558). The two flows share the descriptor-driven
 * fields, the validate-then-save contract, and the typed failure banner; only
 * the copy, the initial values, and the save call differ:
 *
 *   - **connect** — empty fields, all required, calls `connect`.
 *   - **update**  — non-secret fields pre-filled from the stored config; the
 *     secret starts blank with a "leave blank to keep" hint (we never
 *     round-trip the token) and a blank secret is omitted so the stored one is
 *     kept; calls `updateCredentials`.
 *
 * On success the caller reloads into the connected state; on a typed failure
 * the banner shows the reason and nothing is persisted.
 */
function CredentialsDialog({
  open,
  mode,
  descriptor,
  connection,
  onClose,
  onSaved,
  onError,
}: {
  open: boolean
  mode: 'connect' | 'update'
  descriptor: IssueTrackerProviderDescriptor
  /** Required for `update` (the connection being edited); absent for `connect`. */
  connection?: IssueTrackerConnection
  onClose: () => void
  onSaved: () => void
  onError: (msg: string) => void
}) {
  const isUpdate = mode === 'update'
  const label = connectionNameOf(descriptor)

  // Update pre-fills non-secret fields from the stored config; connect starts
  // blank. Secret fields always start blank.
  const initialValues = useMemo(() => {
    const v: Record<string, string> = {}
    for (const f of descriptor.credentialSchema) {
      v[f.key] = isUpdate && !f.secret && connection ? providerConfigValue(connection, f.key) : ''
    }
    return v
    // Re-seed whenever the dialog (re)opens for this connection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connection, descriptor, isUpdate, open])

  const [values, setValues] = useState<Record<string, string>>(initialValues)
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState<IssueTrackerValidationResult | null>(null)

  useEffect(() => {
    if (open) {
      setValues(initialValues)
      setResult(null)
    }
  }, [open, initialValues])

  // Connect requires every field. Update requires the non-secret fields but
  // lets the secret stay blank (= keep the stored token).
  const allFilled = descriptor.credentialSchema.every((f) =>
    isUpdate && f.secret ? true : (values[f.key] ?? '').trim().length > 0,
  )

  const placeholders = isUpdate
    ? Object.fromEntries(
        descriptor.credentialSchema
          .filter((f) => f.secret)
          .map((f) => [f.key, 'Leave blank to keep the current value']),
      )
    : undefined

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!allFilled) return
    setSubmitting(true)
    setResult(null)
    try {
      // Trim every field. For update, omit a blank secret so the backend keeps
      // the stored token; every other field is sent as entered.
      const credentials: Record<string, string> = {}
      for (const f of descriptor.credentialSchema) {
        const val = (values[f.key] ?? '').trim()
        if (isUpdate && f.secret) {
          if (val.length > 0) credentials[f.key] = val
        } else {
          credentials[f.key] = val
        }
      }
      const outcome = isUpdate
        ? await issueTrackersAPI.updateCredentials(connection!.id, credentials)
        : await issueTrackersAPI.connect({ provider: descriptor.provider, credentials })
      if (outcome.ok) {
        onSaved()
      } else {
        setResult(outcome.validation)
      }
    } catch (err) {
      onError(
        err instanceof Error
          ? err.message
          : isUpdate
            ? 'Failed to update the connection'
            : 'Failed to connect the issue tracker',
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !submitting) onClose() }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isUpdate ? `Update ${label} credentials` : `Connect ${label}`}</DialogTitle>
          <DialogDescription>
            {isUpdate
              ? `We check the new credentials before saving them.${hasSecretField(descriptor) ? ' Leave the token blank to keep the current one.' : ''}`
              : 'Store your credentials once here at the workspace. We check the connection before saving it.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={(e) => void handleSubmit(e)}>
          <DialogBody className="space-y-4">
            <CredentialFields
              fields={descriptor.credentialSchema}
              values={values}
              onChange={(key, value) => setValues((prev) => ({ ...prev, [key]: value }))}
              disabled={submitting}
              placeholders={placeholders}
              idPrefix={isUpdate ? 'it-update' : 'it-connect'}
            />
            {result && <ValidationResultBanner result={result} />}
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || !allFilled}>
              {submitting ? 'Checking…' : isUpdate ? 'Save changes' : 'Connect'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
